import { useMemo, useState, useRef, useCallback } from 'react'
import { User, Users, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Membro {
  id: number
  email: string
  nome: string
  tipo: string
  livello: string
  percentuale: number
  attivo: boolean
  sponsor_id: number | null
  depth: number
  stato: 'apc' | 'zero_canone' | 'inattivo' | 'attivo'
  is_apc: boolean
  in_zero_canone: boolean
  vq_rete: number
}

interface TreeNode extends Membro {
  children: TreeNode[]
}

interface OrgChartTreeProps {
  membri: Membro[]
  rootNome: string
  rootLivello: string
  rootPercentuale: number
}

const STATO_COLORS: Record<string, { bar: string; badge: string; label: string }> = {
  apc:        { bar: 'bg-green-500',  badge: 'bg-green-600 text-white',  label: 'APC' },
  zero_canone:{ bar: 'bg-yellow-400', badge: 'bg-yellow-500 text-black', label: 'Zero Canone' },
  attivo:     { bar: 'bg-blue-500',   badge: 'bg-blue-600 text-white',   label: 'Attivo' },
  inattivo:   { bar: 'bg-red-500',    badge: 'bg-red-700 text-white',    label: 'Inattivo' },
}

const LINE_COLOR = '#475569'
const LINE_WIDTH = 2
const DROP_HEIGHT = 28

function buildTree(membri: Membro[]): TreeNode[] {
  const byParent = new Map<number | null, Membro[]>()
  membri.forEach(m => {
    const key = m.sponsor_id
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(m)
  })
  function buildNodes(parentId: number | null): TreeNode[] {
    return (byParent.get(parentId) || []).map(m => ({
      ...m,
      children: buildNodes(m.id)
    }))
  }
  return membri
    .filter(m => m.depth === 1)
    .map(m => ({ ...m, children: buildNodes(m.id) }))
}

function NodeCard({
  nome, livello, stato, tipo, isRoot = false, onClick
}: {
  nome: string
  livello: string
  stato: string
  tipo: string
  isRoot?: boolean
  onClick?: () => void
}) {
  const colors = STATO_COLORS[stato] || STATO_COLORS.inattivo

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex flex-col items-center rounded-lg border overflow-hidden cursor-pointer transition-all',
        'hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10',
        isRoot
          ? 'border-blue-500 bg-slate-700 shadow-md shadow-blue-500/20'
          : 'border-slate-600 bg-slate-800'
      )}
      style={{ width: 120, minWidth: 120 }}
    >
      <div className={cn(
        'flex items-center justify-center w-full',
        isRoot ? 'bg-blue-900/50' : 'bg-slate-700/60'
      )} style={{ height: 52 }}>
        <User className={cn('w-7 h-7', isRoot ? 'text-blue-300' : 'text-slate-400')} />
      </div>
      <div className="px-2 py-1.5 text-center w-full">
        <p className="text-xs font-semibold text-white truncate leading-tight">{nome}</p>
        <p className="text-xs text-slate-400 truncate mt-0.5">{livello}</p>
      </div>
      <div className={cn('h-1.5 w-full', isRoot ? 'bg-blue-500' : colors.bar)} />
    </div>
  )
}

function OrgNode({ node }: { node: TreeNode }) {
  const [collapsed, setCollapsed] = useState(false)
  const hasChildren = node.children.length > 0
  const showChildren = hasChildren && !collapsed

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NodeCard
        nome={node.nome}
        livello={node.livello}
        stato={node.stato}
        tipo={node.tipo}
        onClick={() => hasChildren && setCollapsed(c => !c)}
      />

      {hasChildren && (
        <div
          style={{
            width: LINE_WIDTH,
            height: DROP_HEIGHT,
            background: LINE_COLOR,
            position: 'relative',
            cursor: 'pointer',
          }}
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Espandi' : 'Comprimi'}
        >
          {collapsed && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              background: '#334155',
              border: `1px solid ${LINE_COLOR}`,
              borderRadius: '50%',
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#94a3b8',
              zIndex: 1,
            }}>
              {node.children.length}
            </div>
          )}
        </div>
      )}

      {showChildren && (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {node.children.map((child, i) => {
            const isFirst = i === 0
            const isLast = i === node.children.length - 1
            const isOnly = node.children.length === 1

            return (
              <div
                key={child.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  paddingLeft: 12,
                  paddingRight: 12,
                }}
              >
                {/* Horizontal connector bar at top */}
                {!isOnly && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    height: LINE_WIDTH,
                    background: LINE_COLOR,
                    left: isFirst ? '50%' : 0,
                    right: isLast ? '50%' : 0,
                  }} />
                )}

                {/* Vertical drop to child */}
                <div style={{
                  width: LINE_WIDTH,
                  height: DROP_HEIGHT,
                  background: LINE_COLOR,
                }} />

                <OrgNode node={child} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function OrgChartTree({ membri, rootNome, rootLivello, rootPercentuale }: OrgChartTreeProps) {
  const tree = useMemo(() => buildTree(membri), [membri])

  if (membri.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-500" />
        <p className="text-slate-400">Non hai ancora membri nella tua rete</p>
      </div>
    )
  }

  const [zoom, setZoom] = useState(1)
  const containerRef = useRef<HTMLDivElement>(null)

  const MIN_ZOOM = 0.3
  const MAX_ZOOM = 2.0
  const STEP = 0.15

  const zoomIn  = useCallback(() => setZoom(z => Math.min(MAX_ZOOM, parseFloat((z + STEP).toFixed(2)))), [])
  const zoomOut = useCallback(() => setZoom(z => Math.max(MIN_ZOOM, parseFloat((z - STEP).toFixed(2)))), [])
  const reset   = useCallback(() => setZoom(1), [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      if (e.deltaY < 0) zoomIn()
      else zoomOut()
    }
  }, [zoomIn, zoomOut])

  return (
    <div>
      {/* Toolbar: legend + zoom */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 px-1">
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
          {Object.entries(STATO_COLORS).map(([key, cfg]) => (
            <span key={key} className="flex items-center gap-1.5">
              <span className={cn('w-3 h-3 rounded-sm inline-block', cfg.bar)} />
              {cfg.label}
            </span>
          ))}
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1">
          <button
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <span className="text-xs text-slate-300 font-mono w-12 text-center select-none">
            {Math.round(zoom * 100)}%
          </span>

          <button
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-1 rounded text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="w-px h-4 bg-slate-600 mx-1" />

          <button
            onClick={reset}
            className="p-1 rounded text-slate-400 hover:text-white transition-colors"
            title="Ripristina zoom"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-3 px-1">
        Ctrl + rotella del mouse per zoomare · Clicca su un nodo per espandere/comprimere
      </p>

      {/* Scrollable tree container */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        className="overflow-auto rounded-lg border border-slate-700/50 bg-slate-900/30"
        style={{ minHeight: 320, maxHeight: 660 }}
      >
        {/* Inner wrapper that scales */}
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            transformOrigin: 'top center',
            transform: `scale(${zoom})`,
            transition: 'transform 0.15s ease',
            padding: 32,
            minWidth: 'max-content',
          }}
        >
          {/* Root node */}
          <NodeCard
            nome={rootNome}
            livello={rootLivello}
            stato="attivo"
            tipo="consulente"
            isRoot
          />

          {/* Drop from root to first level */}
          <div style={{ width: LINE_WIDTH, height: DROP_HEIGHT, background: LINE_COLOR }} />

          {/* Top-level children row */}
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {tree.map((node, i) => {
              const isFirst = i === 0
              const isLast  = i === tree.length - 1
              const isOnly  = tree.length === 1
              return (
                <div
                  key={node.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    paddingLeft: 12,
                    paddingRight: 12,
                  }}
                >
                  {!isOnly && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      height: LINE_WIDTH,
                      background: LINE_COLOR,
                      left: isFirst ? '50%' : 0,
                      right: isLast ? '50%' : 0,
                    }} />
                  )}
                  <div style={{ width: LINE_WIDTH, height: DROP_HEIGHT, background: LINE_COLOR }} />
                  <OrgNode node={node} />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
